import { usePointCloudParams } from 'features/pointClouds/components/PointCloudContextProvider'
import { usePointCloudData } from 'features/pointClouds/hooks/usePointCloudData'
import { PointCloudOctree, PointColorType } from 'gecko-potree-core'
import React, {
  Dispatch,
  FC,
  MutableRefObject,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import * as THREE from 'three'
import { Vector2, Vector3 } from 'three'
import {
  GeckoOrganization,
  GeckoPointCloudScene,
  GeckoPotreePointCloud,
  GeckoPotreePointCloudHotkeys,
  GeckoSite,
  GeckoSiteObservation,
  ViewerMode,
} from 'types'
import { useMemoizedObject } from 'utils/hookHelpers'

export const DEFAULT_POINT_BUDGET = 20000000
export const DEFAULT_POINT_SIZE = 0.6
export const DEFAULT_EDL_STRENGTH = 250.0
export const DEFAULT_EDL_RADIUS = 1.4

type PointCloudContextType = {
  // Viewer state
  viewerMode: ViewerMode
  setViewerMode: Dispatch<SetStateAction<ViewerMode>>
  showTiles: boolean
  setShowTiles: Dispatch<SetStateAction<boolean>>
  showPointCloud: boolean
  setShowPointCloud: Dispatch<SetStateAction<boolean>>
  speedBoostLevel: 0 | 1 | 2 | 3
  setSpeedBoostLevel: Dispatch<SetStateAction<0 | 1 | 2 | 3>>

  // Point cloud settings
  enableEdl: boolean
  setEnableEdl: Dispatch<SetStateAction<boolean>>
  edlStrength: number
  setEdlStrength: Dispatch<SetStateAction<number>>
  edlRadius: number
  setEdlRadius: Dispatch<SetStateAction<number>>

  enableWhitePoints: boolean
  setEnableWhitePoints: Dispatch<SetStateAction<boolean>>

  pointBudget: number
  setPointBudget: Dispatch<SetStateAction<number>>
  pointSize: number
  setPointSize: Dispatch<SetStateAction<number>>

  // Popup menu state
  showEditPointCloudConfigPopupMenu: Vector2 | undefined
  setShowEditPointCloudConfigPopupMenu: Dispatch<
    SetStateAction<Vector2 | undefined>
  >

  // Camera and interaction state
  hitPosition: Vector3 | null
  setHitPosition: Dispatch<SetStateAction<Vector3 | null>>
  observationPosition: Vector3 | null
  setObservationPosition: Dispatch<SetStateAction<Vector3 | null>>
  pointCloudMinY: number | undefined
  setPointCloudMinY: Dispatch<SetStateAction<number | undefined>>

  // Temporary while changing rotation
  enableLegacyRotation: boolean
  setEnableLegacyRotation: Dispatch<SetStateAction<boolean>>

  cameraRotation: { pitch: number; yaw: number }
  setCameraRotation: Dispatch<SetStateAction<{ pitch: number; yaw: number }>>
  camera: MutableRefObject<THREE.PerspectiveCamera | null>

  flyToCoordinates: Vector3 | undefined
  setFlyToCoordinates: Dispatch<SetStateAction<Vector3 | undefined>>
  coordinatesToLookAtAfterFlight: Vector3 | undefined
  setCoordinatesToLookAtAfterFlight: Dispatch<
    SetStateAction<Vector3 | undefined>
  >

  // Observation state
  editingObservation: GeckoSiteObservation | null
  setEditingObservation: Dispatch<SetStateAction<GeckoSiteObservation | null>>

  // Point cloud data
  potreePointClouds: readonly GeckoPotreePointCloud[]
  activePointCloud: GeckoPotreePointCloud | undefined
  pointCloudScenes: readonly GeckoPointCloudScene[]
  activePointCloudScene: GeckoPointCloudScene | undefined
  pointCloudOctreesRef: MutableRefObject<PointCloudOctree[]>
  isLoadingPotreePointClouds: boolean

  site: GeckoSite | undefined
  organization: GeckoOrganization | undefined

  // Sidebar state
  isSidebarCollapsed: boolean
  setIsSidebarCollapsed: Dispatch<SetStateAction<boolean>>

  // Color settings
  colorMode: PointColorType
  setColorMode: Dispatch<SetStateAction<PointColorType>>
  hueFilterRange: number[]
  setHueFilterRange: Dispatch<SetStateAction<number[]>>
  gamma: number
  setGamma: Dispatch<SetStateAction<number>>

  // Near clipping settings
  nearClippingDistance: number
  setNearClippingDistance: Dispatch<SetStateAction<number>>
  enableNearClipping: boolean
  setEnableNearClipping: Dispatch<SetStateAction<boolean>>

  enableTooltips: boolean
  setEnableTooltips: Dispatch<SetStateAction<boolean>>

  // Hotkeys
  hotkeys: GeckoPotreePointCloudHotkeys | undefined
}

const PointCloudContext = React.createContext<
  PointCloudContextType | undefined
>(undefined)

const PointCloudContextProvider: React.FC<
  PointCloudContextType & {
    children: React.ReactNode
  }
> = ({ children, ...props }) => {
  const context = useMemoizedObject(props)

  return (
    <PointCloudContext.Provider value={context}>
      {children}
    </PointCloudContext.Provider>
  )
}

export const usePointCloudContext = (): PointCloudContextType => {
  const context = React.useContext(PointCloudContext)
  if (!context)
    throw new Error(
      'usePointCloudContext() must be used within a point cloud context'
    )
  return context
}

export const DefaultPointCloudContextProvider: FC<{
  children: ReactNode
}> = ({ children }) => {
  const params = usePointCloudParams()

  const siteID = params?.siteID
  const pointCloudIDs = params?.pointCloudIDs

  const {
    site: curSite,
    organization: curOrg,
    pointCloudScenes,
    activePointCloudScene,
    potreePointClouds,
    activePointCloud,
    isLoadingPotreePointClouds,
  } = usePointCloudData({ siteID, pointCloudIDs })

  const [viewerMode, setViewerMode] = useState<ViewerMode>(
    ViewerMode.ENV_CONTROLS
  )
  const [showTiles, setShowTiles] = useState(false)
  const [showPointCloud, setShowPointCloud] = useState(true)
  const [speedBoostLevel, setSpeedBoostLevel] = useState<0 | 1 | 2 | 3>(0)

  const [enableEdl, setEnableEdl] = useState<boolean>(false)
  const [enableWhitePoints, setEnableWhitePoints] = useState<boolean>(false)

  const [edlStrength, setEdlStrength] = useState<number>(0)
  const [edlRadius, setEdlRadius] = useState<number>(0)
  const [pointBudget, setPointBudget] = useState<number>(0)
  const [pointSize, setPointSize] = useState<number>(0)

  useEffect(() => {
    const settings = activePointCloud?.data?.default_settings
    setPointBudget(settings?.point_budget ?? DEFAULT_POINT_BUDGET)
    setPointSize(settings?.point_size ?? DEFAULT_POINT_SIZE)
    setEdlRadius(settings?.edl_radius ?? DEFAULT_EDL_RADIUS)
    setEdlStrength(settings?.edl_strength ?? DEFAULT_EDL_STRENGTH)
  }, [activePointCloud])

  const [colorMode, setColorMode] = useState<PointColorType>(PointColorType.RGB)
  const [gamma, setGamma] = useState<number>(2.6)

  const [hueFilterRange, setHueFilterRange] = useState<number[]>([0, 360])

  // Near clipping settings
  const [nearClippingDistance, setNearClippingDistance] = useState<number>(0.0)
  const [enableNearClipping, setEnableNearClipping] = useState<boolean>(false)

  // Tooltip settings
  const [enableTooltips, setEnableTooltips] = useState<boolean>(false)

  const [
    showEditPointCloudConfigPopupMenu,
    setShowEditPointCloudConfigPopupMenu,
  ] = useState<Vector2 | undefined>(undefined)
  const [hitPosition, setHitPosition] = useState<Vector3 | null>(null)
  const [observationPosition, setObservationPosition] =
    useState<Vector3 | null>(null)

  const [cameraRotation, setCameraRotation] = useState<{
    pitch: number
    yaw: number
  }>({ pitch: 0, yaw: 0 })
  const [cameraRef] = useState<
    MutableRefObject<THREE.PerspectiveCamera | null>
  >({ current: null })
  const [editingObservation, setEditingObservation] =
    useState<GeckoSiteObservation | null>(null)

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(true)

  const [pointCloudMinY, setPointCloudMinY] = useState<number | undefined>(
    undefined
  )

  const [enableLegacyRotation, setEnableLegacyRotation] =
    useState<boolean>(false)

  const pointCloudOctreesRef = useRef<PointCloudOctree[]>([])

  const [flyToCoordinates, setFlyToCoordinates] = useState<Vector3 | undefined>(
    undefined
  )
  const [coordinatesToLookAtAfterFlight, setCoordinatesToLookAtAfterFlight] =
    useState<Vector3 | undefined>(undefined)

  const contextValue = useMemo(
    () => ({
      viewerMode,
      setViewerMode,
      showTiles,
      setShowTiles,
      showPointCloud,
      setShowPointCloud,
      speedBoostLevel,
      setSpeedBoostLevel,
      enableEdl,
      setEnableEdl,
      edlStrength,
      setEdlStrength,
      edlRadius,
      setEdlRadius,
      enableWhitePoints,
      setEnableWhitePoints,
      pointBudget,
      setPointBudget,
      pointSize,
      setPointSize,
      showEditPointCloudConfigPopupMenu,
      setShowEditPointCloudConfigPopupMenu,
      hitPosition,
      setHitPosition,
      observationPosition,
      setObservationPosition,
      cameraRotation,
      setCameraRotation,
      camera: cameraRef,
      editingObservation,
      setEditingObservation,
      potreePointClouds,
      activePointCloud,
      pointCloudScenes,
      activePointCloudScene,
      isLoadingPotreePointClouds,
      site: curSite,
      organization: curOrg,
      isSidebarCollapsed,
      setIsSidebarCollapsed,
      hueFilterRange,
      setHueFilterRange,
      colorMode,
      setColorMode,
      gamma,
      setGamma,
      pointCloudMinY,
      setPointCloudMinY,
      enableLegacyRotation,
      setEnableLegacyRotation,
      pointCloudOctreesRef,
      flyToCoordinates,
      setFlyToCoordinates,
      coordinatesToLookAtAfterFlight,
      setCoordinatesToLookAtAfterFlight,
      nearClippingDistance,
      setNearClippingDistance,
      enableNearClipping,
      setEnableNearClipping,
      enableTooltips,
      setEnableTooltips,
      hotkeys: activePointCloud?.data?.hotkeys,
    }),
    [
      viewerMode,
      setViewerMode,
      showTiles,
      setShowTiles,
      showPointCloud,
      setShowPointCloud,
      speedBoostLevel,
      setSpeedBoostLevel,
      enableEdl,
      setEnableEdl,
      edlStrength,
      setEdlStrength,
      edlRadius,
      setEdlRadius,
      enableWhitePoints,
      setEnableWhitePoints,
      pointBudget,
      setPointBudget,
      pointSize,
      setPointSize,
      showEditPointCloudConfigPopupMenu,
      setShowEditPointCloudConfigPopupMenu,
      hitPosition,
      setHitPosition,
      observationPosition,
      setObservationPosition,
      cameraRotation,
      setCameraRotation,
      cameraRef,
      editingObservation,
      setEditingObservation,
      potreePointClouds,
      activePointCloud,
      pointCloudScenes,
      activePointCloudScene,
      isLoadingPotreePointClouds,
      curSite,
      curOrg,
      isSidebarCollapsed,
      setIsSidebarCollapsed,
      hueFilterRange,
      setHueFilterRange,
      colorMode,
      setColorMode,
      gamma,
      setGamma,
      pointCloudMinY,
      setPointCloudMinY,
      enableLegacyRotation,
      setEnableLegacyRotation,
      pointCloudOctreesRef,
      flyToCoordinates,
      setFlyToCoordinates,
      coordinatesToLookAtAfterFlight,
      setCoordinatesToLookAtAfterFlight,
      nearClippingDistance,
      setNearClippingDistance,
      enableNearClipping,
      setEnableNearClipping,
      enableTooltips,
      setEnableTooltips,
    ]
  )

  return (
    <PointCloudContextProvider {...contextValue}>
      {children}
    </PointCloudContextProvider>
  )
}
